/**
 * Leest een datum uit elke vorm waarin deze codebase er een bewaart.
 *
 * Dat is geen luxe maar noodzaak: `Personas.StartDate` staat er 197 keer als
 * kale tekst in (`'2024-01-15'`, de ruwe waarde van een `input[type=date]`) en
 * 9 keer als Firestore-tijdstempel; `MeasureMoments.Moment` is 476 keer een
 * tijdstempel en 76 keer tekst. Een helper die maar één vorm aankan laat de
 * andere stil wegvallen — geen fout, geen waarschuwing, alleen een rij die
 * nergens meer opduikt.
 *
 * Een kale datum wordt gelezen als middernacht in de LOKALE tijdzone, net als
 * `buildDateFromInputs` in `TasksModalContent.jsx` en net als de tijdstempels
 * die `PersonaAbsences` wegschrijft. Zo levert dezelfde dag in beide vormen
 * dezelfde waarde op.
 *
 * AI-CONTEXT: See docs/pitfalls.md#api-callable-timestamp-vs-ms
 *
 * @param {*} value - Firestore-tijdstempel (ook geserialiseerd), milliseconden, of een datumtekst
 * @returns {number|null} Milliseconden, of `null` als er geen datum in zit
 */
const toMillis = (value) => {
    if (value === null || value === undefined || value === '') return null

    if (typeof value === 'number') return Number.isFinite(value) ? value : null

    if (typeof value === 'object') {
        if (typeof value.toMillis === 'function') {
            const ms = value.toMillis()
            return Number.isFinite(ms) ? ms : null
        }
        if (typeof value.toDate === 'function') {
            const ms = value.toDate()?.getTime()
            return Number.isFinite(ms) ? ms : null
        }
        // De vorm waarin een callable (fetchHierarchyData) een Timestamp
        // terugstuurt. Tot op de milliseconde: daarop groeperen we oude
        // inzendingen zonder FormID.
        const seconds = value._seconds ?? value.seconds
        if (typeof seconds === 'number') {
            const ms = seconds * 1000 + Math.floor((value._nanoseconds ?? value.nanoseconds ?? 0) / 1e6)
            return Number.isFinite(ms) ? ms : null
        }
        return null
    }

    if (typeof value !== 'string') return null

    // Een kale dag krijgt lokale middernacht; `Date.parse` zou hier UTC pakken
    // en de dag in een negatieve tijdzone een dag terugzetten.
    const dayOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (dayOnly) {
        const [, year, month, day] = dayOnly
        return new Date(Number(year), Number(month) - 1, Number(day)).getTime()
    }

    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
}

export default toMillis
